import * as Sentry from '@sentry/react';
import React from 'react';
import { VERSION } from '../version';

// Global flag to prevent multiple initializations
let sentryInitialized = false;

/**
 * Initialize Sentry for error tracking and performance monitoring
 */
export function initializeSentry() {
    // Prevent multiple initializations (React StrictMode protection)
    if (sentryInitialized) {
        return;
    }

    // Only initialize in production or when explicitly enabled
    if (import.meta.env.MODE === 'development' && !import.meta.env.VITE_SENTRY_ENABLE_DEV) {
        return;
    }

    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (!dsn) {
        return;
    }

    // Mark as initialized before actual initialization
    sentryInitialized = true;

    Sentry.init({
        dsn,
        environment: import.meta.env.MODE,
        integrations: [
            // Browser Tracing
            Sentry.browserTracingIntegration(),
            // Replay integration for session recordings
            Sentry.replayIntegration(),
        ],
        // Performance monitoring
        tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
        // Session Replay sample rates
        replaysSessionSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
        replaysOnErrorSampleRate: 1.0,
        // Release information using build system version
        release: `snapfy-frontend@${VERSION.buildId}`,
        // Additional build context
        initialScope: {
            tags: {
                buildTime: VERSION.buildTime,
                buildId: VERSION.buildId,
            },
        },
        // Additional configuration
        beforeSend(event) {
            // Filter out non-critical errors in production
            if (import.meta.env.MODE === 'production') {
                // Skip network errors that are common and not actionable
                if (event.exception?.values?.[0]?.type === 'NetworkError') {
                    return null;
                }
            }
            return event;
        },
    });

    Sentry.addBreadcrumb({
        message: 'Sentry initialization completed',
        category: 'sentry',
        level: 'info',
    });

    // Expose for testing
    (window as any).Sentry = Sentry;
}


/**
 * Capture an error with additional context
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
    Sentry.withScope((scope) => {
        if (context) {
            Object.keys(context).forEach(key => {
                scope.setTag(key, String(context[key]));
            });
        }
        Sentry.captureException(error);
    });
}

/**
 * User attributes for analytics tracking
 */
export interface AnalyticsUser {
    id: string;
    email?: string;
    username?: string;
    company?: string;
    role?: 'admin' | 'user';
    creditBalance?: number;
    createdAt?: string;
}

/**
 * Set user context when user logs in
 */
export function setSentryUser(user: AnalyticsUser) {
    Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
    });
    
    // Set additional user context as tags for filtering
    Sentry.setTags({
        'user.company': user.company || 'unknown',
        'user.role': user.role || 'user',
        'user.has_credits': user.creditBalance !== undefined ? (user.creditBalance > 0 ? 'yes' : 'no') : 'unknown',
    });
}

/**
 * Clear user context when user logs out
 */
export function clearSentryUser() {
    Sentry.setUser(null);
}
